import { useEffect } from "react";

import { useDashboardRealtime } from "./useDashboardRealtime";

/**
 * Keeps the auth session in sync with server-side membership changes.
 *
 * The dashboard data socket only connects once an organization exists, so a
 * user parked on onboarding would never learn they were added to an
 * organization out-of-band (invite accepted in another tab, admin add). This
 * hook keeps the realtime connection alive for any authenticated user and
 * refreshes the session when an organization_membership_changed event for
 * this user arrives.
 */
export function useOrganizationMembershipRefresh({
  enabled,
  refreshSession,
  userId,
}: {
  enabled: boolean;
  refreshSession: () => Promise<void>;
  userId: string | null;
}): void {
  const { subscribe } = useDashboardRealtime({ enabled });

  useEffect(() => {
    if (!enabled || !userId) {
      return;
    }
    return subscribe(
      (message) => {
        if (
          message.type === "organization_membership_changed" &&
          message.payload.user_id === userId
        ) {
          void refreshSession();
        }
      },
      { topic: "internalEventTopic" },
    );
  }, [enabled, refreshSession, subscribe, userId]);
}
